import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Store, createSelector } from '@ngrx/store';
import { DropdownModule } from 'primeng/dropdown';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { FreyaCommonModule } from 'src/app/freya-common/freya-common.module';
import { quickJsActions } from 'src/app/quickjs/quickjs.actions';
import { quickjsFeature } from 'src/app/quickjs/quickjs.feature';
import { brandingFeature } from 'src/app/state/branding.store';

import { selectSplitFranchises } from '../../../job-calculator.selectors';

// Requirement: `#6517` - "Split FP Revenue" control. Lets the estimator allocate the calculator's
// totalJobRevenueFP across franchise partners. The selected franchises live in the `fpSplitSelectedIds`
// calculator input (comma-joined ids); each row's optional amount override lives in `fpSplit__<id>`.
// The calculator (calculateState) auto-fills the non-overridden rows and echoes the final per-franchise
// amounts back into calculated state, which this component reads for display. No franchise can be picked
// twice — the per-row dropdown hides franchises already chosen in other rows.
const SELECTED_IDS_KEY = 'fpSplitSelectedIds';
const MIN_ROWS = 2;

function parseIds(raw: unknown): string[] {
  return String(raw ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

interface SplitRow {
  franchiseId: string | null;
  amount: number;
  hasOverride: boolean;
  isEmpty: boolean;
  emptyIndex?: number;
}

@Component({
  selector: 'app-fp-revenue-split-table',
  standalone: true,
  imports: [ CommonModule, FormsModule, FreyaCommonModule, DropdownModule ],
  templateUrl: './fp-revenue-split-table.component.html',
  styleUrl: './fp-revenue-split-table.component.scss',
})
export class FpRevenueSplitTableComponent {

  // Extra empty rows the user has added beyond the baseline minimum.
  private readonly extraEmptyRows$ = new BehaviorSubject<number>(0);

  private readonly pendingAmounts: Record<string, number | null | undefined> = {};

  protected readonly quickJsActions = quickJsActions;

  private readonly baseViewModel$ = this.store.select(createSelector(
    selectSplitFranchises,
    quickjsFeature.selectUserInputs,
    quickjsFeature.selectCalculatedState,
    brandingFeature.selectCurrency,
    (franchises, userInputs, cs, currency) => {
      const selectedIds = parseIds(userInputs[SELECTED_IDS_KEY]);
      const nameById = new Map(franchises.map((f) => [ f.id, f.name ]));

      const committedRows: SplitRow[] = selectedIds.map((id) => {
        const overrideRaw = userInputs[`fpSplit__${id}`];
        return {
          franchiseId: id,
          amount: Number(cs[`fpSplit__${id}`] ?? 0),
          hasOverride: overrideRaw != null && overrideRaw !== '',
          isEmpty: false,
        };
      });

      return {
        franchises,
        nameById,
        selectedIds,
        committedRows,
        currency,
        total: Number(cs['totalJobRevenueFP'] ?? 0),
        remainder: Number(cs['fpSplitRemainder'] ?? 0),
      };
    },
  ));

  readonly viewModel$ = combineLatest([ this.baseViewModel$, this.extraEmptyRows$ ]).pipe(
    map(([ vm, extraEmpty ]) => {
      // Always show at least MIN_ROWS slots until that many franchises are committed; the user
      // can add more via "Add franchise partner".
      const emptyRowCount = Math.max(extraEmpty, Math.max(0, MIN_ROWS - vm.committedRows.length));

      const emptyRows: SplitRow[] = Array.from({ length: emptyRowCount }, (_, i) => ({
        franchiseId: null,
        amount: 0,
        hasOverride: false,
        isEmpty: true,
        emptyIndex: i,
      }));

      const usedIds = new Set(vm.selectedIds);

      const rows = [ ...vm.committedRows, ...emptyRows ].map((row) => ({
        ...row,
        name: row.franchiseId ? (vm.nameById.get(row.franchiseId) ?? row.franchiseId) : '',
        // Options exclude franchises chosen in OTHER rows so a franchise can't be picked twice.
        options: vm.franchises.filter((f) => !usedIds.has(f.id) || f.id === row.franchiseId),
      }));

      return {
        franchises: vm.franchises,
        currency: vm.currency,
        total: vm.total,
        remainder: vm.remainder,
        rows,
        // Passed back into the action handlers from the template so the component holds no
        // store-derived state (@ngrx/no-store-subscription — use the async pipe, not .subscribe).
        selectedIds: vm.selectedIds,
        canAddRow: vm.franchises.length > 0 && vm.selectedIds.length < vm.franchises.length,
      };
    }),
  );

  constructor(protected readonly store: Store) {}

  addRow(): void {
    this.extraEmptyRows$.next(this.extraEmptyRows$.value + 1);
  }

  private commitSelectedIds(ids: string[]): void {
    this.store.dispatch(quickJsActions.inputChanged({ key: SELECTED_IDS_KEY, value: ids.join(',') }));
  }

  onFranchiseSelected(selectedIds: string[], previousId: string | null, newId: string | null): void {
    if (!newId || newId === previousId) { return; }

    const ids = [ ...selectedIds ];

    if (previousId) {
      const idx = ids.indexOf(previousId);
      if (idx >= 0) { ids[idx] = newId; }
      // Drop the old franchise's override so it doesn't linger unreferenced. resetAmount also
      // discards any uncommitted pending value, which would otherwise be committed on blur if the
      // same franchise were re-selected later.
      this.resetAmount(previousId);
    } else {
      if (ids.includes(newId)) { return; }
      ids.push(newId);
      // This empty slot became committed — release one extra empty row if we had added any.
      if (this.extraEmptyRows$.value > 0) {
        this.extraEmptyRows$.next(this.extraEmptyRows$.value - 1);
      }
    }

    this.commitSelectedIds(ids);
  }

  removeRow(selectedIds: string[], row: SplitRow): void {
    if (row.isEmpty || !row.franchiseId) {
      if (this.extraEmptyRows$.value > 0) {
        this.extraEmptyRows$.next(this.extraEmptyRows$.value - 1);
      }
      return;
    }

    const ids = selectedIds.filter((id) => id !== row.franchiseId);
    // resetAmount clears the pending value as well as the stored override, so a half-typed amount
    // can't be committed later if this franchise is added back.
    this.resetAmount(row.franchiseId);
    this.commitSelectedIds(ids);
  }

  onAmountInput(franchiseId: string, value: number | null): void {
    this.pendingAmounts[franchiseId] = value;
  }

  commitAmount(franchiseId: string): void {
    if (!(franchiseId in this.pendingAmounts)) { return; }
    const value = this.pendingAmounts[franchiseId];
    delete this.pendingAmounts[franchiseId];
    const key = `fpSplit__${franchiseId}`;
    if (value == null) {
      this.store.dispatch(quickJsActions.inputReset({ key }));
    } else {
      this.store.dispatch(quickJsActions.inputChanged({ key, value }));
    }
  }

  resetAmount(franchiseId: string): void {
    delete this.pendingAmounts[franchiseId];
    this.store.dispatch(quickJsActions.inputReset({ key: `fpSplit__${franchiseId}` }));
  }

  trackByRow(_index: number, row: SplitRow): string {
    return row.franchiseId ?? `empty-${row.emptyIndex}`;
  }
}
